import React, { createContext, useState, useEffect, useCallback, useContext, useRef } from 'react';
import api from '../api';
import { AuthContext } from './AuthContext';

export const AppContext = createContext();
export const AppProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext);
  const [continueWatching, setContinueWatching] = useState([]);
  const [watchHistory, setWatchHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const requestInProgress = useRef(false);
  const lastRequestTime = useRef(0);
  const REQUEST_COOLDOWN = 10000; // 10 seconds between requests
  const dataCache = useRef({
    continueWatching: null,
    watchHistory: null,
    timestamp: 0
  });
  const CACHE_LIFETIME = 60000; // cache expires after 1 minute
  const refreshTimeoutRef = useRef(null);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const loadUserData = useCallback(async (forceRefresh = false) => {
    if (!currentUser) {
      setContinueWatching([]);
      setWatchHistory([]);
      return;
    }
    
    const now = Date.now();
    
    // use cache if available and fresh
    if (!forceRefresh && 
        dataCache.current.continueWatching && 
        dataCache.current.watchHistory && 
        now - dataCache.current.timestamp < CACHE_LIFETIME) {
      console.log('Using cached user data');
      setContinueWatching(dataCache.current.continueWatching);
      setWatchHistory(dataCache.current.watchHistory);
      return;
    }
    
    // avoid multiple api calls
    if (requestInProgress.current) {
      console.log('Request already in progress, skipping...');
      return;
    }
    
    // rate limiting protection
    if (!forceRefresh && now - lastRequestTime.current < REQUEST_COOLDOWN) {
      console.log('Request cooldown active, skipping...');
      return;
    }
    
    try {
      requestInProgress.current = true;
      setLoading(true);
      lastRequestTime.current = now;
      console.log('Fetching user data for:', currentUser.username);
      
      // Fetch continue watching first (higher priority)
      const continueResponse = await api.get('/user/continue-watching');
      
      if (continueResponse.data.success && continueResponse.data.data) {
        const continueData = continueResponse.data.data;
        setContinueWatching(continueData);
        dataCache.current.continueWatching = continueData;
      } else {
        setContinueWatching([]);
        dataCache.current.continueWatching = [];
      }
      
      // Then fetch watch history
      const historyResponse = await api.get('/user/history');
      
      if (historyResponse.data.success && historyResponse.data.data) {
        const historyData = historyResponse.data.data;
        setWatchHistory(historyData);
        dataCache.current.watchHistory = historyData;
      } else {
        setWatchHistory([]);
        dataCache.current.watchHistory = [];
      }
      
      // Update cache timestamp
      dataCache.current.timestamp = now;
      
    } catch (error) {
      console.error('Error loading user data:', error);
      setContinueWatching([]);
      setWatchHistory([]);
    } finally {
      setLoading(false);
      requestInProgress.current = false;
    }
  }, [currentUser]);

  // Add a debounced version of refreshUserData
  const debouncedRefreshUserData = useCallback((forceRefresh = false) => {
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    // Set a new timeout
    refreshTimeoutRef.current = setTimeout(() => {
      loadUserData(forceRefresh);
      refreshTimeoutRef.current = null;
    }, 1000); // 1 second debounce
  }, [loadUserData]);

  // Load user data when user changes
  useEffect(() => {
    if (currentUser) {
      // Small timeout to prevent immediate API call on mount
      const timer = setTimeout(() => {
        loadUserData();
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setContinueWatching([]);
      setWatchHistory([]);
    }
  }, [loadUserData, currentUser]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  return (
    <AppContext.Provider value={{
      continueWatching,
      watchHistory,
      loading,
      sidebarOpen,
      toggleSidebar,
      closeSidebar,
      refreshUserData: debouncedRefreshUserData
    }}>
      {children}
    </AppContext.Provider>
  );
};